import { Avatar, Box, Checkbox, Dialog, FormControlLabel, Stack, Tooltip, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { AutopayInfoWrapper } from 'src/components/global-styles/GlobalStyles';

import DEFAULT_USER from '../../assets/images/defaultProfile.svg';
import { colors, theme } from '../../components';
import CustomButton from '../../components/button/ButtonComponent';
import { OtpInput } from '../../components/otp-input/otp-input';
import { PhoneInputComponent } from '../../components/text-field/PhoneInputComponent';
import { TextFieldComponent } from '../../components/text-field/text-field-component';
import CommonHeader from '../../components/ui-components/CommonHeader';
import CommonIconAvatar from '../../components/ui-components/CommonIconAvatar';
import { createSupportTicket, getSupportList, SupportRequestType } from '../../services/support-service';
import { addPhoneNumber, resendPhoneOtp, verifyPhoneOtp } from '../../services/user-service';
import { RootState } from '../../store';
import { openToast } from '../../store/reducers/toastSlice';
import { trackPhoneNumberAdded, trackPhoneVerificationFailed, trackPhoneVerified } from '../../utils/google-analytics';
import {
    defaultTimerCount,
    getDialCodeForApi,
    getMaskedPhoneNumberForProp,
    restrictedExtensions,
} from '../../utils/helper';
import { useTranslation } from '../../utils/translations';

import { MyProfileWrapper } from './SettingStyles';

interface MyProfileTabProps {
    firstName: string;
    lastName: string;
    email: string;
    mobileNumber: string;
    onFirstNameChange: (value: string) => void;
    onLastNameChange: (value: string) => void;
    onMobileNumberChange: (value: string) => void;
    onProfilePictureUpload: (file: File) => void;
    isEditMode: boolean;
    verifiedPhoneNumber: boolean;
    onVerifyPhoneNumber: () => void;
    verifiedAlternatePhoneNumber: boolean;
    onVerifyAlternatePhoneNumber: () => void;
    alternativeMobileNumber?: string;
    onAlternativeMobileNumberChange?: (value: string) => void;
    onRefreshUserDetails?: () => Promise<void>;
    onEditDetails?: () => void;
    onSaveDetails?: () => void;
    isSaveDisabled?: boolean;
    firstNameError?: string;
    lastNameError?: string;
    alternativePhoneError?: string;
    onSetAlternativePhoneError?: (error: string) => void;
    mobileNumberError?: string;
    onSetMobileNumberError?: (error: string) => void;
    address?: {
        street: string;
        city: string;
        state: string;
        country: string;
        postalCode: string;
    };
    requestTypes?: SupportRequestType[];
    contracts?: Array<{ id: string; name: string }>;
    isPrimary?: boolean;
}

export const MyProfileTab: React.FC<MyProfileTabProps> = ({
    firstName,
    lastName,
    email,
    mobileNumber,
    onFirstNameChange,
    onLastNameChange,
    onMobileNumberChange,
    onProfilePictureUpload,
    isEditMode,
    verifiedPhoneNumber,
    onVerifyPhoneNumber,
    verifiedAlternatePhoneNumber,
    onVerifyAlternatePhoneNumber,
    alternativeMobileNumber,
    onAlternativeMobileNumberChange,
    onRefreshUserDetails,
    onEditDetails,
    onSaveDetails,
    isSaveDisabled,
    firstNameError,
    lastNameError,
    alternativePhoneError,
    onSetAlternativePhoneError,
    mobileNumberError,
    onSetMobileNumberError,
    address,
    requestTypes = [],
    contracts = [],
    isPrimary = true,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const userImageLink = useSelector((state: RootState) => state.user.userImageLink);
    const [showOtpModal, setShowOtpModal] = useState(false);
    const [timerCount, setTimerCount] = useState(0);
    const [currentVerifyingField, setCurrentVerifyingField] = useState<'primary' | 'alternate' | null>(null);
    const [verifyingPhoneNumber, setVerifyingPhoneNumber] = useState('');
    const impersonation = useSelector((state: RootState) => state.impersonation);

    const [smsConsentChecked, setSmsConsentChecked] = useState(false);

    // Country code state for phone input
    const [countryCode, setCountryCode] = useState('+1'); // Primary
    const [countryReCode, setCountryReCode] = useState('+1'); // Alternate

    // Address update modal state
    const [showAddressModal, setShowAddressModal] = useState(false);
    const [newAddress, setNewAddress] = useState('');
    const [isSubmittingAddress, setIsSubmittingAddress] = useState(false);
    const [isAddressUpdateDisabled, setIsAddressUpdateDisabled] = useState(false);
    const initialAddressCheckFetch = useRef(false);

    useEffect(() => {
        if (initialAddressCheckFetch.current) return;
        initialAddressCheckFetch.current = true;

        const checkForExistingAddressTicket = async () => {
            try {
                const response = await getSupportList(false, 1, 100);
                if (response.statusCode === 200 && Array.isArray(response.data)) {
                    const addressTicket = response.data.find(ticket => {
                        const subjectHasAddressUpdate =
                            ticket.Subject?.toLowerCase().includes('address update') ||
                            ticket.Description?.toLowerCase().includes('address update');
                        return subjectHasAddressUpdate;
                    });

                    if (addressTicket) {
                        setIsAddressUpdateDisabled(true);
                    }
                }
            } catch {
                // Silently handle errors - ticket check is non-critical
            }
        };

        checkForExistingAddressTicket();
    }, []);

    const hasAddressData = (): boolean => {
        const { street, city, state, country, postalCode } = address || {};
        return !!(street || city || state || country || postalCode);
    };

    const formatLegalAddress = (): string => {
        if (!hasAddressData()) {
            return isPrimary
                ? t('settings.myProfile.legalAddressNotFound')
                : t('settings.myProfile.secondaryUserNoLegalAddress');
        }

        const { street, city, state, country, postalCode } = address || {};

        const capitalizeWords = (str: string): string => {
            return str
                .toLowerCase()
                .split(' ')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
        };

        const parts = [];
        if (street) parts.push(capitalizeWords(street));
        if (city) parts.push(capitalizeWords(city));

        const statePostal = [state, postalCode].filter(Boolean).join(' ');
        if (statePostal) parts.push(statePostal);
        if (country) parts.push(country);

        return parts.join(', ');
    };

    const handleOpenOtpModal = async (field: 'primary' | 'alternate') => {
        const phoneToVerify = field === 'primary' ? mobileNumber : alternativeMobileNumber;
        const phoneExtension = getDialCodeForApi(field === 'primary' ? countryCode : countryReCode);

        if (!phoneToVerify) {
            dispatch(
                openToast({
                    show: true,
                    isAlertOpen: true,
                    message: 'Please Enter A Phone Number',
                    severity: 'error',
                }),
            );
            return;
        }

        // Validate alternative phone number is not the same as primary phone
        if (field === 'alternate' && mobileNumber === alternativeMobileNumber) {
            if (onSetAlternativePhoneError) {
                onSetAlternativePhoneError(
                    'Alternate Phone Number Cannot Be The Same As Primary Phone Number',
                );
            }
            return;
        }

        // Validate primary phone number is not the same as alternative phone
        if (field === 'primary' && alternativeMobileNumber && mobileNumber === alternativeMobileNumber) {
            if (onSetMobileNumberError) {
                onSetMobileNumberError('Phone Number Cannot Be The Same As Alternate Phone Number');
            }
            return;
        }

        try {
            const response = await addPhoneNumber({
                phoneNumber: phoneToVerify,
                phoneExtension,
                isPrimary: field === 'primary' ? 1 : 0,
            });

            if (response?.statusCode === 200) {
                trackPhoneNumberAdded();
                setTimerCount(defaultTimerCount);
                setCurrentVerifyingField(field);
                setVerifyingPhoneNumber(phoneToVerify);
                setShowOtpModal(true);
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: 'Passcode Sent Successfully.',
                        severity: 'success',
                    }),
                );
            } else {
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: response?.message || 'Failed To Send Passcode',
                        severity: 'error',
                    }),
                );
            }
        } catch {
            dispatch(
                openToast({
                    show: true,
                    isAlertOpen: true,
                    message: 'Error Sending Passcode. Please Try Again.',
                    severity: 'error',
                }),
            );
        }
    };

    const handleCloseOtpModal = () => {
        setShowOtpModal(false);
        setCurrentVerifyingField(null);
        setVerifyingPhoneNumber('');
    };

    const handleVerifyOtp = async (otp: number | string) => {
        if (!verifyingPhoneNumber) {
            dispatch(
                openToast({
                    show: true,
                    isAlertOpen: true,
                    message: 'Phone Number Not Found',
                    severity: 'error',
                }),
            );
            return;
        }

        const phoneExtension = getDialCodeForApi(currentVerifyingField === 'primary' ? countryCode : countryReCode);

        try {
            const response = await verifyPhoneOtp({
                phoneNumber: verifyingPhoneNumber,
                otp: typeof otp === 'string' ? parseInt(otp, 10) : otp,
                phoneExtension,
                isPrimary: currentVerifyingField === 'primary' ? 1 : 0,
            });

            if (response?.statusCode === 200) {
                trackPhoneVerified();
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: 'Phone Number Verified Successfully.',
                        severity: 'success',
                    }),
                );

                // Update verification status immediately
                if (currentVerifyingField === 'primary') {
                    onVerifyPhoneNumber();
                } else if (currentVerifyingField === 'alternate') {
                    onVerifyAlternatePhoneNumber();
                }

                // Refresh user details from API to get latest verified phone numbers and update original values
                if (onRefreshUserDetails) {
                    try {
                        await onRefreshUserDetails();
                    } catch (error) {
                        console.error('Error refreshing user details after verification:', error);
                    }
                }

                setShowOtpModal(false);
                setCurrentVerifyingField(null);
                setVerifyingPhoneNumber('');
            } else {
                trackPhoneVerificationFailed(response.message);
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: response.message || 'Failed To Verify Passcode',
                        severity: 'error',
                    }),
                );
            }
        } catch {
            trackPhoneVerificationFailed('exception');
            dispatch(
                openToast({
                    show: true,
                    isAlertOpen: true,
                    message: 'Error Verifying Passcode. Please Try Again.',
                    severity: 'error',
                }),
            );
        }
    };

    const handleResendOTP = async () => {
        try {
            const phoneToResend = currentVerifyingField === 'primary' ? mobileNumber : alternativeMobileNumber;
            const phoneExtension = getDialCodeForApi(currentVerifyingField === 'primary' ? countryCode : countryReCode);

            if (!phoneToResend) {
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: 'Phone Number Is Missing',
                        severity: 'error',
                    }),
                );
                return;
            }

            const response = await resendPhoneOtp({
                phoneNumber: phoneToResend,
                phoneExtension,
            });
            setTimerCount(defaultTimerCount);
            if (response?.statusCode === 200) {
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: 'Passcode Resent Successfully.',
                        severity: 'success',
                    }),
                );
            } else {
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: response.message || 'Failed To Resend Passcode',
                        severity: 'error',
                    }),
                );
            }
        } catch (error) {
            dispatch(
                openToast({
                    show: true,
                    isAlertOpen: true,
                    message: error instanceof Error ? error.message : 'Error Resending Passcode. Please Try Again.',
                    severity: 'error',
                }),
            );
        }
    };
    const handleOpenAddressModal = () => {
        setNewAddress('');
        setShowAddressModal(true);
    };

    const handleCloseAddressModal = () => {
        setShowAddressModal(false);
        setNewAddress('');
    };

    const handleSubmitAddressUpdate = async () => {
        if (!newAddress || newAddress.trim() === '') {
            dispatch(
                openToast({
                    show: true,
                    isAlertOpen: true,
                    message: t('settings.myProfile.addressUpdateMessages.emptyAddress'),
                    severity: 'error',
                }),
            );
            return;
        }

        if (newAddress.length > 300) {
            dispatch(
                openToast({
                    show: true,
                    isAlertOpen: true,
                    message: t('settings.myProfile.addressUpdateMessages.exceedsMaxLength'),
                    severity: 'error',
                }),
            );
            return;
        }

        try {
            setIsSubmittingAddress(true);
            const otherRequestType = requestTypes.find(type => type?.name?.toLowerCase() === 'others');
            const requestTypeId = otherRequestType?.id || 8;

            const response = await createSupportTicket(
                {
                    subject: 'Address Update Request',
                    description: `Address Update: ${newAddress}`,
                    contractId: contracts[0]?.id || '',
                    requestType: requestTypeId,
                    otherReason: 'Address Update Request',
                },
                true,
            );

            if (response.statusCode === 200 || response.statusCode === 201) {
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: t('settings.myProfile.addressUpdateMessages.submitSuccess'),
                        severity: 'success',
                    }),
                );
                handleCloseAddressModal();
                setIsAddressUpdateDisabled(true);
            } else {
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: response.message || t('settings.myProfile.addressUpdateMessages.submitError'),
                        severity: 'error',
                    }),
                );
            }
        } catch {
            dispatch(
                openToast({
                    show: true,
                    isAlertOpen: true,
                    message: t('settings.myProfile.addressUpdateMessages.submitException'),
                    severity: 'error',
                }),
            );
        } finally {
            setIsSubmittingAddress(false);
        }
    };

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }

        // Get file extension
        const fileName = file.name.toLowerCase();
        const fileExtension = fileName.substring(fileName.lastIndexOf('.') + 1);

        // Check if file extension is restricted
        if (restrictedExtensions.includes(fileExtension)) {
            dispatch(
                openToast({
                    show: true,
                    isAlertOpen: true,
                    message: `Security: File type .${fileExtension} is Not Allowed. Please Upload Only Image Files (JPEG, PNG, WebP).`,
                    severity: 'error',
                }),
            );
            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            return;
        }

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            dispatch(
                openToast({
                    show: true,
                    isAlertOpen: true,
                    message: 'Unsupported File Format. Please Upload a JPEG, PNG, or WebP Image.',
                    severity: 'error',
                }),
            );
            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            return;
        }

        // Validate file size (max 5MB)
        const maxSize = 5 * 1024 * 1024; // 5MB
        if (file.size > maxSize) {
            dispatch(
                openToast({
                    show: true,
                    isAlertOpen: true,
                    message: 'File Size Exceeds the 5 MB Limit. Please Upload a Smaller File.',
                    severity: 'error',
                }),
            );
            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            return;
        }

        onProfilePictureUpload(file);

        // Reset file input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    return (
        <MyProfileWrapper>
            {/* Section Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap={'wrap'} gap={2}>
                <CommonHeader
                    mainTitle={t('settings.myProfile.title')}
                    mainTitleFS={20}
                    subTitle={t('settings.myProfile.subTitle')}
                />

                {!isEditMode && (
                    <CustomButton
                        variant="outlined"
                        onClick={onEditDetails}
                        sx={{
                            width: 'fit-content',
                            minWidth: 144,
                            pointerEvents: impersonation?.isImpersonating ? 'none' : 'auto',
                            cursor: impersonation?.isImpersonating ? 'not-allowed' : 'pointer',
                        }}
                    >
                        {t('settings.myProfile.editProfile')}
                    </CustomButton>
                )}

                {isEditMode && (
                    <Stack direction="row" spacing={1} sx={{ display: 'flex' }}>
                        <CustomButton
                            variant="contained"
                            onClick={onSaveDetails}
                            sx={{
                                width: 'fit-content',
                                minWidth: 144,
                                pointerEvents: impersonation?.isImpersonating ? 'none' : 'auto',
                                cursor: impersonation?.isImpersonating ? 'not-allowed' : 'pointer',
                            }}
                            disabled={isSaveDisabled}
                        >
                            {t('settings.myProfile.save')}
                        </CustomButton>

                        {/* <CustomButton variant="outlined" onClick={onCancelDetails} sx={{ width: 'fit-content', minWidth: 144, }}>
                            {t('settings.myProfile.cancel')}
                        </CustomButton> */}
                    </Stack>
                )}

                {/* <CustomButton
                    variant="outlined"
                    onClick={() => setOpenChangePasswordModal(true)}
                    sx={{ width: 'fit-content' }}
                    SMButton={true}
                >
                    {t('settings.myProfile.changePassword')}
                </CustomButton> */}
            </Box>
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 7, lg: 6 }}>
                    <Stack gap={3} height={'100%'}>
                        {/* First Name and Last Name in Same Row */}
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextFieldComponent
                                    label={t('settings.myProfile.firstName')}
                                    value={firstName || ''}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                        onFirstNameChange(e.target.value)
                                    }
                                    variant="outlined"
                                    disabled={!isEditMode}
                                    error={!!firstNameError}
                                    helperText={firstNameError}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextFieldComponent
                                    label={t('settings.myProfile.lastName')}
                                    value={lastName || ''}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                        onLastNameChange(e.target.value)
                                    }
                                    variant="outlined"
                                    disabled={!isEditMode}
                                    error={!!lastNameError}
                                    helperText={lastNameError}
                                />
                            </Grid>
                        </Grid>

                        <TextFieldComponent
                            label={t('settings.myProfile.emailId')}
                            value={email || ''}
                            onChange={() => {}}
                            variant="outlined"
                            disabled
                        />

                        <Stack direction="row" gap={2} alignItems="center">
                            <Tooltip title={formatLegalAddress()} arrow>
                                <div style={{ flex: 1 }}>
                                    <TextFieldComponent
                                        label={t('settings.myProfile.legalAddress')}
                                        value={formatLegalAddress()}
                                        onChange={() => {}}
                                        variant="outlined"
                                        disabled
                                        multiline
                                        minRows={2}
                                        sx={{ flex: 1 }}
                                    />
                                </div>
                            </Tooltip>

                            {isPrimary && (
                                <Stack gap={1} alignItems="flex-start" width="fit-content">
                                    <CustomButton
                                        variant="contained"
                                        onClick={handleOpenAddressModal}
                                        sx={{
                                            width: 'fit-content',
                                            whiteSpace: 'nowrap',
                                            pointerEvents:
                                                impersonation?.isImpersonating || isAddressUpdateDisabled
                                                    ? 'none'
                                                    : 'auto',
                                            cursor:
                                                impersonation?.isImpersonating || isAddressUpdateDisabled
                                                    ? 'not-allowed'
                                                    : 'pointer',
                                        }}
                                        disabled={
                                            isSaveDisabled || isAddressUpdateDisabled || impersonation?.isImpersonating
                                        }
                                    >
                                        Update Address
                                    </CustomButton>
                                </Stack>
                            )}
                        </Stack>

                        {isPrimary && isAddressUpdateDisabled && (
                            <AutopayInfoWrapper direction="row" alignItems="center" mb={1}>
                                <span className="icon-info-circle" />
                                <Typography variant="body2">
                                    {t('settings.myProfile.addressUpdateInProgress')}
                                </Typography>
                            </AutopayInfoWrapper>
                        )}

                        {!isPrimary && hasAddressData() && (
                            <AutopayInfoWrapper direction="row" alignItems="center" mb={1}>
                                <span className="icon-info-circle" />
                                <Typography variant="body2">
                                    {t('settings.myProfile.addressUpdateNotAvailableForAssociates')}
                                </Typography>
                            </AutopayInfoWrapper>
                        )}

                        <PhoneInputComponent
                            label={t('settings.myProfile.mobileNumber')}
                            value={mobileNumber || ''}
                            onChange={e => {
                                onMobileNumberChange(e.target.value);
                            }}
                            variant="outlined"
                            disabled={!isEditMode}
                            maxLength={10}
                            error={!!mobileNumberError}
                            errorValue={mobileNumberError}
                            countryCode={countryCode}
                            onCountryChange={setCountryCode}
                            endIcon={
                                isEditMode ? (
                                    !verifiedPhoneNumber ? (
                                        <Stack className="verifyButton">
                                            <CustomButton
                                                variant="contained"
                                                sx={{ width: 'fit-content' }}
                                                SMButton
                                                onClick={() => handleOpenOtpModal('primary')}
                                                disabled={!mobileNumber || mobileNumber.length < 10}
                                            >
                                                Verify
                                            </CustomButton>
                                        </Stack>
                                    ) : (
                                        <Stack className="verifyButton">
                                            <span
                                                className="icon-check"
                                                style={{ color: colors.successPreset[10], fontSize: 16 }}
                                            />
                                        </Stack>
                                    )
                                ) : null
                            }
                        />

                        <Stack direction={'row'} gap={2} alignItems={'end'} position={'relative'}>
                            <PhoneInputComponent
                                label={t('settings.myProfile.phoneNumber')}
                                value={alternativeMobileNumber || ''}
                                onChange={e => {
                                    onAlternativeMobileNumberChange?.(e.target.value);
                                }}
                                variant="outlined"
                                disabled={!isEditMode}
                                maxLength={10}
                                error={!!alternativePhoneError}
                                errorValue={alternativePhoneError}
                                countryCode={countryReCode}
                                onCountryChange={setCountryReCode}
                                endIcon={
                                    isEditMode ? (
                                        !verifiedAlternatePhoneNumber ? (
                                            <Stack className="verifyButton">
                                                <CustomButton
                                                    variant="contained"
                                                    sx={{ width: 'fit-content' }}
                                                    SMButton
                                                    onClick={() => handleOpenOtpModal('alternate')}
                                                    disabled={
                                                        !alternativeMobileNumber || alternativeMobileNumber.length < 10
                                                    }
                                                >
                                                    Verify
                                                </CustomButton>
                                            </Stack>
                                        ) : (
                                            <Stack className="verifyButton">
                                                <span
                                                    className="icon-check"
                                                    style={{ color: colors.successPreset[10], fontSize: 16 }}
                                                />
                                            </Stack>
                                        )
                                    ) : null
                                }
                            />
                        </Stack>

                        {isEditMode && (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={smsConsentChecked}
                                        onChange={e => setSmsConsentChecked(e.target.checked)}
                                        size="small"
                                        sx={{ pt: 0 }}
                                    />
                                }
                                label={
                                    <Typography variant="body2" color="textSecondary">
                                        I agree to receive SMS/passcode messages for account verification and security
                                        purposes. Message and data rates may apply. (Optional)
                                    </Typography>
                                }
                                sx={{ alignItems: 'flex-start' }}
                            />
                        )}
                    </Stack>
                </Grid>
                <Grid size={{ xs: 12, sm: 5, lg: 6 }}>
                    <Stack sx={{ alignItems: 'center', justifyContent: 'center' }} height={'100%'}>
                        <Stack sx={{ position: 'relative' }}>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/jpeg,image/jpg,image/png,image/webp"
                                style={{ display: 'none' }}
                                onChange={handleFileChange}
                            />
                            <Avatar className="avatarWrapper">
                                <img src={userImageLink || DEFAULT_USER} alt="Profile" className="userImage" />
                            </Avatar>
                            <Box
                                onClick={handleUploadClick}
                                sx={{
                                    position: 'absolute',
                                    bottom: -10,
                                    right: 81,
                                    pointerEvents: impersonation?.isImpersonating ? 'none' : 'auto',
                                    cursor: impersonation?.isImpersonating ? 'not-allowed' : 'pointer',
                                }}
                            >
                                <CommonIconAvatar
                                    icon="icon-plus"
                                    iconColor={colors.fullWhite}
                                    iconBorderRadius={50}
                                    iconPadding={1.6}
                                    iconSize={24}
                                    iconBgColor={colors.primaryLight[70]}
                                    iconBorder={colors.fullWhite}
                                />
                            </Box>
                        </Stack>
                    </Stack>
                </Grid>
            </Grid>

            {/* Passcode verification modal */}
            <Dialog
                open={showOtpModal}
                onClose={(_event, reason) => {
                    // Only allow closing via close button, not backdrop click or escape key
                    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                        return;
                    }
                    handleCloseOtpModal();
                }}
                disableEscapeKeyDown
                sx={{
                    '& .MuiDialog-paper': {
                        borderRadius: 4,
                        padding: 3,
                        maxWidth: '400px',
                        width: '100%',
                    },
                }}
                aria-labelledby="otp-dialog-title"
                aria-describedby="otp-dialog-description"
            >
                <OtpInput
                    mainTitle="Enter Passcode"
                    subtitle={
                        <>
                            Please enter a {6}-digit Passcode sent to registered mobile -{' '}
                            <span style={{ fontFamily: theme.typography.fontFamily }}>
                                {getMaskedPhoneNumberForProp(verifyingPhoneNumber || '')}
                            </span>
                        </>
                    }
                    length={6}
                    onChange={(otp: string) => console.log(otp)}
                    onVerify={handleVerifyOtp}
                    verifyButtonText="Verify"
                    onClose={handleCloseOtpModal}
                    onResend={handleResendOTP}
                    timerCount={timerCount}
                    resendTextWithTimer={'Resend Passcode'}
                    isCalledFromLogin={false}
                />
            </Dialog>

            {/* Address Update Modal */}
            <Dialog
                open={showAddressModal}
                onClose={(_event, reason) => {
                    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                        return;
                    }
                    handleCloseAddressModal();
                }}
                disableEscapeKeyDown
                sx={{
                    '& .MuiDialog-paper': {
                        borderRadius: 4,
                        padding: 3,
                        maxWidth: '500px',
                        width: '100%',
                    },
                }}
            >
                <Stack gap={3}>
                    <Stack>
                        <Typography variant="h6" fontWeight={600} mb={1}>
                            {t('settings.myProfile.updateAddressModal.title')}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            {t('settings.myProfile.updateAddressModal.subtitle')}
                        </Typography>
                    </Stack>

                    <TextFieldComponent
                        label={t('settings.myProfile.updateAddressModal.label')}
                        value={newAddress || ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            if (e.target.value.length <= 300) {
                                setNewAddress(e.target.value);
                            }
                        }}
                        variant="outlined"
                        multiline
                        minRows={3}
                        placeholder={t('settings.myProfile.updateAddressModal.placeholder')}
                        sx={{ width: '100%' }}
                    />

                    <Typography variant="caption" color="textSecondary">
                        {`${newAddress.length}/300`}
                    </Typography>

                    <Stack direction="row" gap={2} justifyContent="flex-end">
                        <CustomButton
                            variant="outlined"
                            onClick={handleCloseAddressModal}
                            disabled={isSubmittingAddress}
                            sx={{ width: 'fit-content' }}
                        >
                            {t('settings.myProfile.updateAddressModal.cancel')}
                        </CustomButton>
                        <CustomButton
                            variant="contained"
                            onClick={handleSubmitAddressUpdate}
                            disabled={isSubmittingAddress || !newAddress.trim()}
                            sx={{ width: 'fit-content' }}
                        >
                            {isSubmittingAddress
                                ? t('settings.myProfile.updateAddressModal.submitting')
                                : t('settings.myProfile.updateAddressModal.continue')}
                        </CustomButton>
                    </Stack>
                </Stack>
            </Dialog>
        </MyProfileWrapper>
    );
};
